import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Flame, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleAuth = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.email, form.password, form.name || form.email.split("@")[0]);
      }
      toast.success("Welcome to ExamBro");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-stone-900 text-stone-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-orange-600">
              <Flame className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-2xl font-bold tracking-tight">ExamBro</span>
          </div>
        </div>
        <div className="relative space-y-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-400">
            The Strava for exam preparation
          </div>
          <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
            Turn any past paper into a fully simulated exam.
          </h1>
          <p className="max-w-md text-stone-300 leading-relaxed">
            Upload a PDF. Get a real timed exam. Get a deep report on accuracy, speed, pressure and decision quality.
            Track improvement across every attempt.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-stone-700">
            {[
              { k: "Silent", v: "Telemetry" },
              { k: "AI", v: "Grading" },
              { k: "Historical", v: "Trends" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{s.k}</div>
                <div className="font-heading text-lg font-semibold">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative font-mono text-xs text-stone-500">Works with any MCQ / integer-answer paper</div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-stone-50">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-orange-600 text-white">
              <Flame className="h-5 w-5" />
            </div>
            <span className="font-heading text-2xl font-bold">ExamBro</span>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Get started</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-8">
            {mode === "login" ? "Sign in to continue" : "Create your account"}
          </h2>

          <Button
            data-testid="google-signin-btn"
            variant="outline"
            className="w-full h-11 mb-6 border-stone-300 hover:bg-stone-100"
            onClick={googleAuth}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-stone-50 px-3 font-mono uppercase tracking-[0.2em] text-stone-400">or with email</span>
            </div>
          </div>

          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Sign up</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <p className="mt-6 text-xs text-stone-500">
            By continuing you agree to our terms. No credit card needed.
          </p>
        </div>
      </div>
    </div>
  );
}
